import React, { createContext, useContext, useState, ReactNode, useEffect } from "react";
import { GetAllInducteesQuery } from "@graphqlTypes";

interface InducteeLandingContextType {
  headlineEl: HTMLElement | null;
  headerEl: HTMLElement | null;
  contentEl: HTMLElement | null;
  setHeadlineEl: (ref: HTMLElement | null) => void;
  setHeaderEl: (ref: HTMLElement | null) => void;
  setContentEl: (ref: HTMLElement | null) => void;
  setScrollCoef: (value: number) => void;
  allInductees: GetAllInducteesQuery['allInductees'];
  inducteesLoading: boolean;
  scrollCoef: number;
  tabAffix: boolean;
  setTabAffix: (affix: boolean) => void;
  extraFixedTab: boolean;
  setExtraFixedTab: (value: boolean) => void;
}

const InducteeLandingContext = createContext<
  InducteeLandingContextType | undefined
>(undefined);

interface InducteeLandingProviderProps {
  children: ReactNode;
}

export const InducteeLandingProvider: React.FC<
  InducteeLandingProviderProps
> = ({ children }) => {
  const [headlineEl, setHeadlineEl] = useState<HTMLElement | null>(null);
  const [headerEl, setHeaderEl] = useState<HTMLElement | null>(null);
  const [contentEl, setContentEl] = useState<HTMLElement | null>(null);
  const [scrollCoef, setScrollCoef] = useState(0);
  const [tabAffix, setTabAffix] = useState<boolean>(false);
  const [extraFixedTab, setExtraFixedTab] = useState<boolean>(false);
  const [allInductees, setAllInductees] = useState<GetAllInducteesQuery['allInductees']>([]);
  const [inducteesLoading, setInducteesLoading] = useState<boolean>(true);

  const fetchAllInductees = async () => {
    const response = await fetch('/api/all-inductees/');
    if (response.ok) {
      const data = await response.json();
      setAllInductees(data);
      setInducteesLoading(false);
    } else {
      console.error('Failed to fetch inductees');
    }
  }

  useEffect(() => {
    fetchAllInductees();
  }, []);

  return (
    <InducteeLandingContext.Provider
      value={{
        headlineEl,
        setHeadlineEl,
        headerEl,
        setHeaderEl,
        contentEl,
        setContentEl,
        setScrollCoef,
        scrollCoef,
        tabAffix,
        setTabAffix,
        extraFixedTab,
        setExtraFixedTab,
        allInductees,
        inducteesLoading,
      }}
    >
      {children}
    </InducteeLandingContext.Provider>
  );
};

export const useInducteeLanding = () => {
  const context = useContext(InducteeLandingContext);
  return (
    context || {
      headlineEl: null as HTMLElement | null,
      headerEl: null as HTMLElement | null,
      contentEl: null as HTMLElement | null,
      setHeadlineEl: () => {},
      setHeaderEl: () => {},
      setContentEl: () => {},
      setScrollCoef: () => {},
      scrollCoef: 0,
      tabAffix: false,
      setTabAffix: () => {},
      extraFixedTab: false,
      setExtraFixedTab: () => {},
      allInductees: [] as GetAllInducteesQuery['allInductees'],
      inducteesLoading: true,
    }
  );
};
